<template>
	<div
		class="main-content"
		:class="{
			'finance-mobile-shell': isRefined,
			'finance-mobile-menu-open': dossierMenuOpen,
			'pointer-events-none':
				selectedDossier?.versions?.versionData === undefined,
		}"
	>
		<button
			v-if="isRefined"
			type="button"
			class="finance-mobile-menu-toggle"
			style="pointer-events: auto"
			:aria-expanded="dossierMenuOpen"
			aria-controls="finance-dossier-menu"
			@click="dossierMenuOpen = !dossierMenuOpen"
		>
			Dossiermenu
		</button>
		<TheSidebar :id="isRefined ? 'finance-dossier-menu' : undefined" />
		<RouterView v-if="dossierStore.selectedDossier" v-slot="{ Component }">
			<KeepAlive include="FinancieringPage">
				<component :is="Component" />
			</KeepAlive>
		</RouterView>
	</div>
</template>

<script setup lang="ts">
import { useQuery, useZero } from "@/composables/useZero";
import { useAddressMetaStore } from "@/store/useAddressMetaStore";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { useDossierStore } from "@/store/useDossierStore";
import { useUserStore } from "@/store/useUserStore";
import { mutators, queries } from "@loanlink-nl/zero";
import { isEqual } from "es-toolkit";
import { computed, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { isRefinedLayoutRoute } from "@/helpers/refinedLayoutRoutes";

useAddressMetaStore();

const dossierStore = useDossierStore();
const dossierDataStore = useDossierDataStore();
const userStore = useUserStore();

const route = useRoute();
const isRefined = computed(() => isRefinedLayoutRoute(route.name));
const dossierMenuOpen = ref(false);
watch(
	() => route.fullPath,
	() => {
		dossierMenuOpen.value = false;
	},
);
const { data: selectedDossier } = useQuery(() => {
	const displayId = Number(route.params.dossierDisplayId);
	return queries.dossier.byId({
		field: "displayId",
		id: Number.isFinite(displayId) ? displayId : -1,
	});
});

watch(
	selectedDossier,
	(dossier) => {
		dossierStore.setSelected(dossier);

		if (dossier) {
			document.title = `${dossier.name} - Loanlink`;
			if (["intermediary", "admin"].includes(userStore.role ?? "")) {
				const zero = useZero();
				zero.value.mutate(
					mutators.dossier.setLastOpened({
						id: dossier.id,
						lastOpened: Date.now(),
					}),
				);
			}
		} else {
			document.title = "Loanlink";
			dossierDataStore.$reset();
		}
	},
	{ immediate: true },
);

onUnmounted(() => {
	dossierStore.setSelected(undefined);
	dossierDataStore.$reset();
});

watch(
	() => selectedDossier.value?.versions?.versionData,
	async (data, previousData) => {
		if (data === undefined) {
			return;
		}

		if (isEqual(data?.data, dossierDataStore.data)) {
			return;
		}

		dossierDataStore.setData(data, previousData === undefined);
	},
	{ immediate: true, deep: true },
);
</script>
